/*
 * The band and the event sub-header — the two structural primitives every
 * temporal view is built from.
 *
 * THE SHARED GRAMMAR (§9, §10, §11) is the same in all three views, which is why
 * these live in one place: band → serif-weight event sub-headers → cards of
 * fencer rows. Only the AXIS changes. Upcoming and Past invert it with the
 * grouping toggle, and Active is inherently tournament-scoped.
 *
 * HEADERS ARE NEVER LINKS (§9, build rule 5). Capability pills are. A band's
 * name is not clickable, so the whole band is available as a collapse target in
 * Past without ambiguity — which is exactly why §11 can make it a full-width
 * button there.
 *
 * PLURAL CHROME FOR PLURAL DATA (principle 9) is the CALLER's decision, not
 * this component's: Active renders a tournament band only when two or more
 * tournaments are live. This component has no idea how many there are, and
 * giving it one would put the rule in the wrong place.
 */

import type { ReactNode } from "react";

import styles from "./Band.module.css";

export interface BandProps {
  /** The band's subject: a tournament name, or a fencer's in the fencer grouping. */
  name: string;

  /**
   * The heading level for the name.
   *
   * A REAL HEADING, and the level is the caller's because only the caller knows
   * the document's shape. §6 puts the structural work on typography, which makes
   * it tempting to render a styled <div> — but then a screen-reader user cannot
   * navigate the screen by heading, and on Active the event names are precisely
   * the landmarks they would navigate by. Typography carries the VISUAL
   * hierarchy; the markup still has to carry the real one.
   */
  headingLevel?: 2 | 3;

  /** Dates, location — whatever the view's spec puts on the band's lines. */
  meta?: ReactNode;

  /**
   * The deadline. Rendered in its OWN CELL of the facts grid and never wrapped
   * into the meta text — which KNOWINGLY narrows §10's "own full row"
   * (decision 363): the rule's point was that the deadline must not join a
   * comma-run with the location, and a grid cell keeps that property while
   * letting a wide band spend one row instead of three. Absent when there is
   * no deadline, which renders nothing rather than an empty cell.
   */
  deadline?: ReactNode;

  /**
   * The quietest line, rendered AFTER the deadline (decision 327).
   *
   * A slot rather than more `meta`, purely so the ORDER is stated rather than
   * emergent. The deadline is the reason Upcoming exists — §10 gives it its own
   * row for that reason — and freshness is the least urgent thing on the band, so
   * anything folded into `meta` would render above the deadline and quietly
   * demote it. Two named slots make that ordering a decision the caller cannot
   * get wrong by appending in the wrong place.
   */
  footer?: ReactNode;

  /** The capability pill, anchored to the band's inline end. */
  action?: ReactNode;

  /**
   * Drops the tint, the border and the padding, leaving a plain heading block.
   *
   * FOR THE TOP TIER OF THE GROUPED VIEWS (decision 326). Upcoming and Past had
   * the tournament in a tinted box and the event as bare text, which inverted how
   * the tiers actually read: the bare text scanned as "new topic", so events
   * detached from the tournament they belonged to. The emphasis moved down a
   * level — the tournament is now a plain heading and the EVENT takes the box,
   * attached to its card of fencers.
   *
   * Active keeps the boxed band and does not pass this. Its middle tier carries
   * round state and staleness rather than being a quiet label, so it is not the
   * same problem.
   */
  plain?: boolean;
}

export function Band({
  name,
  meta,
  deadline,
  footer,
  action,
  headingLevel = 2,
  plain = false,
}: BandProps) {
  const Heading = `h${headingLevel}` as "h2" | "h3";

  return (
    <div className={plain ? `${styles.band} ${styles.bandPlain}` : styles.band}>
      <div className={styles.text}>
        {/*
          THE NAME KEEPS ITS OWN ROW (decision 363, Kevin's pick): tournament
          names run long ("2026 Fencing Center of Chicago RYC & RJCC"), and a
          heading squeezed into a column reads worse than the row it saves.
          Only the FACTS below reflow.
        */}
        <Heading className={plain ? `${styles.name} ${styles.namePlain}` : styles.name}>
          {name}
        </Heading>
        {/*
          The facts grid: up to three columns when the band is wide, collapsing
          to fewer as the container narrows — in rem, so a larger default font
          crosses the thresholds sooner and gets fewer columns (the
          accessibility half of decision 363). Null slots render nothing — no
          placeholder, no empty cell (contract §0).
        */}
        {meta || deadline || footer ? (
          <div className={styles.facts}>
            {meta ? <div className={styles.meta}>{meta}</div> : null}
            {deadline ? <div className={styles.deadline}>{deadline}</div> : null}
            {footer ? <div className={styles.factCell}>{footer}</div> : null}
          </div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export interface EventSubHeaderProps {
  /** The event's name. */
  name: string;

  /** The heading level. See BandProps.headingLevel — same reasoning. */
  headingLevel?: 2 | 3;

  /**
   * The second line. In Upcoming it is the date plus the venue-local start time
   * or "time not published"; in Active it is the round state plus the "as of".
   */
  meta?: ReactNode;

  /**
   * Where the meta sits (decision 364, Kevin's review of 363):
   *
   *   "inline"  — beside the name, wrapping beneath it when they do not fit.
   *               Past and Active, which he ruled fine as they are.
   *   "end"     — pushed to the inline end of the row, so a scan down the
   *               header column reads names on the left and times on the
   *               right. Upcoming's tournament axis.
   *   "stacked" — beneath the name, the pre-363 rendering. The fencer axis's
   *               tournament sub-header, whose meta is a stack of facts that
   *               read as a weird second column beside the name.
   */
  metaLayout?: "inline" | "end" | "stacked";

  /**
   * Let the meta wrap inside itself rather than move as one block (decision
   * 661), for a meta made of unbreakable pieces with break points between them:
   * a split event's two tier times on Upcoming's tournament axis, with
   * metaLayout "end".
   *
   *   off — the meta's width is its whole one-line width, so a line too long to
   *         share the row drops beneath the name whole. Every other sub-header.
   *   on  — the meta stays beside the name while its WIDEST PIECE fits there,
   *         and its pieces wrap inside it; the name is centred against it
   *         rather than baseline-aligned to its first line. Only when not even
   *         one piece fits beside the name does it drop beneath.
   */
  metaWraps?: boolean;

  /**
   * A line of its own BENEATH the name and meta, whatever their widths (Kevin,
   * after the 2026 October NAC). Active's "as of": beside the round state it
   * read as a stray fragment in a different size and colour, so it always takes
   * the next row -- name and round state share a line where they fit, and on a
   * narrow screen the round state wraps under the name and this goes third.
   */
  below?: ReactNode;

  /** The capability pill: "View" in Active and Past. */
  action?: ReactNode;

  /**
   * Recedes the event's name.
   *
   * For §9's went-quiet state, which asks for a "muted header". A prop rather
   * than a selector reaching in from the screen's stylesheet, because CSS modules
   * scope these class names — a parent cannot target `.subHeaderName` from
   * another module, and a global escape hatch to do so would be the one place the
   * scoping was defeated.
   *
   * Colour and not opacity: the name is a WORD, and §6 dims data rather than
   * words. Muted still has to be readable.
   */
  muted?: boolean;

  /**
   * Gives the sub-header the tint and padding, with its trailing corners squared
   * so it sits flush on the card beneath it.
   *
   * ATTACHED, NOT MERELY BOXED, and that is the whole point (decision 326). A
   * tinted header floating above a separate card would just move the "new topic"
   * problem down a tier. Header and card together form ONE object — the header
   * loses its block-end border and the card loses its leading corners, so the
   * seam between them is a single hairline, and an event plus its fencers reads
   * as one thing under the tournament's heading.
   */
  attached?: boolean;
}

/**
 * The event sub-header. §9 outdents it to the inline start of the card edge with
 * no band and no rule, so the hierarchy is carried by position and weight rather
 * than by another box — which is the "typography does the structural work"
 * temperament of §6.
 */
export function EventSubHeader({
  name,
  meta,
  action,
  muted = false,
  attached = false,
  headingLevel = 3,
  metaLayout = "inline",
  metaWraps = false,
  below,
}: EventSubHeaderProps) {
  const Heading = `h${headingLevel}` as "h2" | "h3";

  const textClass = [
    styles.subText,
    metaLayout === "stacked" ? styles.subTextStacked : null,
    metaWraps ? styles.subTextWraps : null,
  ]
    .filter(Boolean)
    .join(" ");
  const metaClass = [
    styles.meta,
    metaLayout === "end" ? styles.metaEnd : null,
    metaWraps ? styles.metaWraps : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={attached ? `${styles.subHeader} ${styles.subHeaderAttached}` : styles.subHeader}>
      {/*
        A WRAPPING row rather than a stack (decisions 363, 364): the meta sits
        on the name's line when the two fit — beside it, or pushed to the
        inline end per metaLayout — and wraps beneath it when they do not,
        which is the old phone rendering. Content-driven, so a larger default
        font wraps sooner with no breakpoint to maintain. "stacked" opts back
        out for meta that is itself a stack of facts.
      */}
      <div className={textClass}>
        <Heading className={muted ? styles.subHeaderNameMuted : styles.subHeaderName}>
          {name}
        </Heading>
        {meta ? <div className={metaClass}>{meta}</div> : null}
        {below ? <div className={`${styles.meta} ${styles.below}`}>{below}</div> : null}
      </div>
      {action}
    </div>
  );
}
